import React, { FunctionComponent } from 'react';
import gql from 'graphql-tag';

import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import CampaignMini from '../CampaignMini/CampaignMini';
import CharityMini from '../CharityMini/CharityMini';
import { CauseMiniFragment } from '../../../types/__generated__';
import { filterByTypename } from '../../utils/graphql';
import AboutOwnerMiniSkeleton from '../AboutOwner/AboutOwnerMiniSkeleton';
import { captureException } from '@sentry/browser';

const FRAGMENT = buildQuery(gql`
  fragment CauseMini on Page {
    id
    relationships {
      beneficiaries(first: 6) {
        nodes {
          ... on Charity {
            impactStatement
            id
            legacyId
            logo
            name
            profilePageUrl
            registrationNumber
            slug
          }
        }
        totalCount
      }
      campaigns(first: 5) {
        nodes {
          id
          url
          logo
          title
          summary
        }
        totalCount
      }
    }
  }
`);

const AboutCauseMini: FunctionComponent = () => {
  const result = usePageQuery<CauseMiniFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (result.loading) {
    return (
      <AboutOwnerMiniSkeleton
        animated
        onRefresh={result.refetch}
        displayRefreshButton={false}
      />
    );
  }

  if (result.error) {
    captureException(result.error);
    return <AboutOwnerMiniSkeleton onRefresh={result.refetch} />;
  }

  if (
    !UsePageQueryResult.hasData(result) ||
    !result.data.relationships ||
    (result.data.relationships.beneficiaries.totalCount === 0 &&
      result.data.relationships.campaigns.totalCount === 0)
  ) {
    return null;
  }

  const {
    relationships: {
      beneficiaries: { nodes: beneficiaryNodes, totalCount: totalCharityCount },
      campaigns: { nodes: campaigns, totalCount: totalCampaignCount },
    },
  } = result.data;

  const charities = filterByTypename(beneficiaryNodes, 'Charity');

  if (totalCampaignCount > 0 && totalCharityCount === 1) {
    const [
      { name, registrationNumber, slug: charitySlug, logo: charityLogo },
    ] = charities;
    return (
      <CampaignMini
        totalCount={totalCampaignCount}
        campaigns={campaigns.map(({ id, title, logo, url, summary }) => ({
          id,
          title,
          logo: logo ?? charityLogo,
          url,
          summary,
        }))}
        charityName={name || ''}
        charityRegistrationNumber={registrationNumber || ''}
        charityShortName={charitySlug}
      />
    );
  }

  if (totalCharityCount > 0) {
    return (
      <CharityMini
        totalCount={totalCharityCount}
        charities={charities.map(charity => ({
          id: charity.id,
          title: charity.name || '',
          logo: charity.logo,
          url: `/${charity.slug}`,
          registrationNumber: charity.registrationNumber || '',
          impactStatement: charity.impactStatement || '',
        }))}
      />
    );
  }

  return null;
};

export default AboutCauseMini;
